import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Product, LocalCurrency } from '../types';
import LocalStorage from '../helper/storage';
import { StoreEventTracker } from '../helper/analytics-tracker';

export interface CartItem extends Product {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image: string;
  discountPrice: number;
  currency: LocalCurrency;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: number) => void;
  clearCart: () => void;
  updateQuantity: (productId: number, quantity: number) => void;
  cartCount: number;
  cartTotal: number;
  isCartOpen: boolean;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(([]));
  const [isCartOpen, setIsCartOpen] = useState(false);

  const getLocalStorageItems = useCallback(async () => {
    try {
      const items = await LocalStorage.getItem<CartItem[]>('cart');
      if (items) {
        setCartItems(items);
      }
    } catch (error) {
      console.error('Failed to retrieve cart items from local storage:', error);
    }
  },[]);

  useEffect(() => {
    getLocalStorageItems();
  }, [])

  const saveStorageItem = useCallback(() => LocalStorage.saveItem<CartItem[]>('cart', cartItems), [cartItems]);

  useEffect(() => {
    void saveStorageItem();
  }, [cartItems])

  const addToCart = useCallback((product: Product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);

      if (existingItem) {
        // Track adding additional quantity
        StoreEventTracker.trackAddToCart(product.id.toString(), 1, product.price);
        
        return prevItems.map(item => item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : { ...item }
        );
      }

      // Track adding new item to cart
      StoreEventTracker.trackAddToCart(product.id.toString(), 1, product.price);

      return [...prevItems, { ...product, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCartItems(prevItems => {
      const itemToRemove = prevItems.find(item => item.id === productId);
      
      if (itemToRemove) {
        // Track item removal
        StoreEventTracker.trackRemoveFromCart(
          productId.toString(), 
          itemToRemove.quantity, 
          itemToRemove.price
        );
      }
      
      return prevItems.filter(item => item.id !== productId);
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === productId);
      
      if (existingItem && existingItem.quantity !== quantity) {
        const quantityDiff = quantity - existingItem.quantity;
        
        if (quantityDiff > 0) {
          // Track quantity increase
          StoreEventTracker.trackAddToCart(productId.toString(), quantityDiff, existingItem.price);
        } else {
          // Track quantity decrease
          StoreEventTracker.trackRemoveFromCart(productId.toString(), Math.abs(quantityDiff), existingItem.price);
        }
      }
      
      return prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
    });
  }, [removeFromCart]);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce((sum, item) => sum +  item.price  * item.quantity, 0);

  const toggleCart = useCallback(() => setIsCartOpen(!isCartOpen), []);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        cartCount,
        cartTotal,
        isCartOpen,
        toggleCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};